import { Button, FormControl, FormHelperText, FormLabel, Icon, Select, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { Regulation, useUpdateOrganizationMutation } from "../../../../autogen/bff-api";
import { SUPPORTED_REGULATIONS } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../common/redux/reducers/authStateReducer";

interface FormValues {
  defaultRegulation: Regulation | "";
}

interface Props {
  currentValue: Regulation | null;
}

export const ChangeDefaultRegulation = ({ currentValue }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const [editOrg, { isLoading }] = useUpdateOrganizationMutation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const displayer = useApiError();
  const { register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { defaultRegulation: currentValue ?? "" },
  });

  const selected = watch("defaultRegulation");

  const onSubmit = async (values: FormValues) => {
    const result = await editOrg({
      orgId: authState.selectedOrg.id,
      editOrganizationRequest: { defaultRegulation: { value: values.defaultRegulation || null } },
    });
    if ("data" in result) {
      dispatch(updateSelectedOrg(result.data));
      toast({ title: t("Default regulation has been updated!"), status: "success" });
    } else {
      displayer.trigger(result.error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl id="defaultRegulation" mt="20px">
        <FormLabel>{t("Default regulation")}</FormLabel>
        <Select placeholder={t("Select the regulation you procure under most often") ?? ""} {...register("defaultRegulation")}>
          {SUPPORTED_REGULATIONS.map((regulation) => (
            <option key={regulation} value={regulation}>
              {t(regulation)}
            </option>
          ))}
        </Select>
        <FormHelperText>
          {t(
            "New procurements start on this regulation. Which one governs follows from what the individual procurement serves, so it can be overridden per procurement",
          )}
        </FormHelperText>
      </FormControl>
      {(currentValue ?? "") !== selected && (
        <Button
          mt="10px"
          leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
          variant="solid"
          colorScheme="teal"
          size="sm"
          type="submit"
          isLoading={isLoading}
        >
          {t("Save change")}
        </Button>
      )}
    </form>
  );
};
